import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';
import SmartImage from './SmartImage';
import { formatCurrency } from '../utils/formatters';
import { useToast } from '../contexts/ToastContext';
import QuantityInput from './common/QuantityInput';
import './Cart.css';

interface CartItem {
  id: string;
  product: {
    id: number;
    sku: string;
    name: string;
    description?: string;
    fullname?: string;
    image_url?: string;
    current_price: number;
    available_quantity: number;
    minimum_order_quantity: number;
  };
  quantity: number;
  price_at_time: number;
  subtotal: number;
  is_bundled_item?: boolean;
  bundled_with_product_id?: number;
}

interface CartData {
  id: string;
  item_count: number;
  subtotal: number;
}

interface EditingState {
  [key: string]: string;
}

export const Cart: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showWarning } = useToast();
  const [cart, setCart] = useState<CartData | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(100); // Default fallback
  const [shippingMinimumDollars, setShippingMinimumDollars] = useState(12); // Default $12
  const [shippingPercentage, setShippingPercentage] = useState(0.1); // Default 10%
  const [draftNotes, setDraftNotes] = useState('');
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftCreated, setDraftCreated] = useState<any>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingQuantities, setEditingQuantities] = useState<EditingState>({});

  // Format draft number from Q-2025-0911-0001 to DFT-0911-0001
  const formatDraftNumber = (quoteNumber: string) => {
    if (!quoteNumber) return '';
    const parts = quoteNumber.split('-');
    if (parts.length >= 4) {
      return `DFT-${parts[2]}-${parts[3]}`;
    }
    return `DFT-${quoteNumber.slice(-8)}`;
  };
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDesktop = windowWidth >= 992;

  // Detect screen size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch settings from API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/grouped');
        setMinimumOrderAmount(response.data.minimum_order_amount);
        setShippingMinimumDollars(response.data.shipping_minimum_dollars);
        setShippingPercentage(response.data.shipping_percentage);
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Keep default values
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (user) {
      fetchCart();
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const response = await api.get('/cart/');
      setCart(response.data.cart);

      // Sort items: group bundled items with their parent products
      const rawItems = response.data.items || [];

      // Create a map of parent product IDs to their bundled items
      const bundledItemsMap = new Map<number, CartItem[]>();
      const parentItems: CartItem[] = [];

      // Separate parent items from bundled items
      rawItems.forEach((item: CartItem) => {
        if (item.is_bundled_item && item.bundled_with_product_id) {
          // This is a bundled item
          const parentId = item.bundled_with_product_id;
          if (!bundledItemsMap.has(parentId)) {
            bundledItemsMap.set(parentId, []);
          }
          bundledItemsMap.get(parentId)!.push(item);
        } else {
          // This is a parent/normal item
          parentItems.push(item);
        }
      });

      // Sort parent items by price descending
      parentItems.sort((a, b) => b.price_at_time - a.price_at_time);

      // Build final sorted list: parent followed by its bundled items
      const sortedItems: CartItem[] = [];
      parentItems.forEach(parent => {
        sortedItems.push(parent);
        // Add bundled items right after their parent
        const bundledItems = bundledItemsMap.get(parent.product.id) || [];
        sortedItems.push(...bundledItems);
      });

      setItems(sortedItems);
      setError('');

      // Update cart count in header/navbar
      const totalItems = sortedItems.reduce((sum, item) => {
        if (item.is_bundled_item) return sum;
        return sum + item.quantity;
      }, 0);
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { itemCount: totalItems } }));
    } catch (err: any) {
      setError('Failed to load cart');
      console.error('Cart fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId: string, newQuantity: number) => {
    // If quantity is 0, remove the item from cart
    if (newQuantity === 0) {
      await removeItem(itemId);
      return;
    }

    if (newQuantity < 1) return;

    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      const response = await api.put(`/cart/items/${itemId}`, {
        quantity: newQuantity
      });
      // Update local state immediately
      const updatedItems = items.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity, subtotal: item.price_at_time * newQuantity } : item
      );
      setItems(updatedItems);

      // Notify Layout to update cart badge immediately with new count (exclude bundled items)
      const totalItems = updatedItems.reduce((sum, item) => {
        if (item.is_bundled_item) return sum;
        return sum + item.quantity;
      }, 0);
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { itemCount: totalItems } }));

      // Refresh cart data in background
      fetchCart();
      setEditingItem(null);
      setEditingQuantities({});
    } catch (err: any) {
      setError('Failed to update quantity');
      console.error('Update quantity error:', err);
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const startEditing = (itemId: string, currentQuantity: number) => {
    setEditingItem(itemId);
    setEditingQuantities({ [itemId]: currentQuantity.toString() });
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditingQuantities({});
  };

  const handleQuantityChange = (itemId: string, value: string) => {
    setEditingQuantities({ ...editingQuantities, [itemId]: value });
  };

  const saveQuantity = async (itemId: string, minQuantity: number) => {
    const value = editingQuantities[itemId];
    
    if (!value || value.trim() === '') {
      cancelEditing();
      return;
    }
    
    const newQuantity = parseInt(value);
    const minimumQty = minQuantity || 1; // Default to 1 if undefined
    
    if (!isNaN(newQuantity) && newQuantity >= minimumQty && newQuantity > 0) {
      await updateQuantity(itemId, newQuantity);
    } else {
      showWarning(`Please enter a valid quantity (minimum: ${minimumQty})`);
      cancelEditing();
    }
  };

  const removeItem = async (itemId: string, isBundled?: boolean) => {
    if (isBundled) {
      setError('This item is bundled with another product and cannot be removed separately. Remove the main product instead.');
      return;
    }

    try {
      await api.delete(`/cart/items/${itemId}`);
      // Update local state immediately
      const updatedItems = items.filter(item => item.id !== itemId);
      setItems(updatedItems);

      // Notify Layout to update cart badge immediately with new count (exclude bundled items)
      const totalItems = updatedItems.reduce((sum, item) => {
        if (item.is_bundled_item) return sum;
        return sum + item.quantity;
      }, 0);
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { itemCount: totalItems } }));

      // Refresh cart data in background
      fetchCart();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to remove item';
      setError(errorMessage);
      console.error('Remove item error:', err);
    }
  };

  const createDraft = async () => {
    setCreatingDraft(true);
    setError('');

    try {
      const response = await api.post('/drafts/', {
        notes: draftNotes,
        expires_in_days: 30
      });

      setDraftCreated(response.data);
      setShowDraftModal(false);
      setDraftNotes('');

      // Clear cart after draft creation
      await fetchCart();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to create draft';
      setError(errorMessage);
      console.error('Create draft error:', err);
      console.error('Error details:', err.response?.data);
    } finally {
      setCreatingDraft(false);
    }
  };

  const clearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your cart? This action cannot be undone.')) {
      return;
    }

    try {
      await api.post('/cart/clear');
      // Update local state immediately
      setItems([]);

      // Notify Layout to update cart badge immediately with count 0
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { itemCount: 0 } }));

      // Refresh cart data in background
      fetchCart();
      setError('');
    } catch (err: any) {
      setError('Failed to clear cart. Please try again.');
      console.error('Clear cart error:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading cart...</span>
            </div>
            <p className="mt-3">Loading cart...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!user) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please log in to view your cart
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ 
        maxWidth: '1300px', 
        margin: '0 auto', 
        padding: '20px 15px'
      }}>
            <h1 className="mb-4" style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              <i className="fas fa-shopping-cart me-2"></i>
              Shopping Cart
            </h1>
            
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>{error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}
            
            {draftCreated && (
              <div className="alert alert-success" style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span><i className="fas fa-check-circle me-2"></i>Draft Created Successfully!</span>
                <span>
                  Draft Number: <Link to={`/drafts/${draftCreated.draft_id}`} className="fw-bold text-decoration-none" style={{ color: '#1565C0' }}>
                    {formatDraftNumber(draftCreated.draft_number)}
                  </Link>
                </span>
                <span>Total: {formatCurrency(draftCreated.total_amount)}</span>
                <span>Valid for 30 days</span>
              </div>
            )}
            
            {items.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                  <h4>Your cart is empty</h4>
                  <p className="text-muted">Start browsing our products to add items to your cart.</p>
                  <a href="/products" className="btn" style={{
                    backgroundColor: 'var(--color-success, #2E7D32)',
                    color: 'white',
                    border: 'none'
                  }}>
                    Browse Products
                  </a>
                </div>
              </div>
            ) : (
              <>
                {/* Bundled Items Notice - Better styling */}
                {items.some(item => item.is_bundled_item) && (
                  <div className="mb-4" style={{
                    backgroundColor: '#e8f4fd',
                    border: 'none',
                    borderLeft: '4px solid #2196F3',
                    borderRadius: '4px',
                    padding: '14px 16px'
                  }}>
                    <div className="d-flex align-items-start">
                      <i className="fas fa-info-circle me-2 mt-1" style={{ color: '#2196F3' }}></i>
                      <div style={{ fontSize: '14px' }}>
                        <strong style={{ color: '#1565C0' }}>Bundled Items Notice:</strong>
                        <div className="mt-1" style={{ color: '#546E7A', lineHeight: '1.5' }}>
                          This order includes a bundled item that is automatically added and may not be removed.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              <div style={{ 
                display: 'flex',
                flexWrap: isDesktop ? 'nowrap' : 'wrap',
                gap: '24px',
                alignItems: 'flex-start'
              }}>
                <div className="cart-items-section" style={{
                  flex: isDesktop ? '1 1 auto' : '0 0 100%',
                  minWidth: 0
                }}>
                  <div className="card mb-4" style={{
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div className="card-header" style={{
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #e9ecef',
                      padding: '16px 20px'
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0" style={{ fontWeight: '600', color: '#2c3e50' }}>
                          Cart Items ({cart?.item_count})
                        </h5>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={clearCart}
                          disabled={!cart || cart.item_count === 0}
                          title="Clear all items from cart"
                        >
                          <i className="fas fa-trash-alt me-1"></i>
                          Clear Cart
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      {!isDesktop ? (
                        // Mobile Card Layout
                        <div className="p-3">
                          {items.map(item => (
                            <div
                              key={item.id}
                              className={`card mb-3 ${item.is_bundled_item ? 'bundled-item' : ''}`}
                              style={{
                                backgroundColor: item.is_bundled_item ? '#f8f9fa' : 'white',
                                borderLeft: item.is_bundled_item ? '4px solid #6c757d' : 'none'
                              }}
                            >
                              <div className="card-body p-3">
                                {/* Product Info */}
                                <div className="d-flex align-items-start mb-3">
                                  <div style={{ width: '80px', height: '80px', minWidth: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '4px', backgroundColor: '#f8f9fa' }} className="me-3">
                                    <SmartImage
                                      src={item.product.image_url || `/images/products/${item.product.id}`}
                                      alt={item.product.name}
                                      className="cart-item-image"
                                      productName={item.product.name}
                                      productSku={item.product.sku}
                                    />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div>
                                        <Link 
                                          to={`/products/${item.product.id}`} 
                                          className="text-decoration-none text-dark"
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <h6 className="mb-1 text-primary">{item.product.description || item.product.fullname || item.product.name}</h6>
                                        </Link>
                                        <small className="text-muted">{item.product.sku}</small>
                                      </div>
                                      {!item.is_bundled_item && (
                                        <button
                                          onClick={() => removeItem(item.id, item.is_bundled_item)}
                                          title="Remove item"
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            padding: '4px 8px',
                                            fontSize: '18px'
                                          }}
                                        >
                                          <i className="fas fa-trash-alt"></i>
                                        </button>
                                      )}
                                    </div>
                                    {item.is_bundled_item && (
                                      <span className="badge bundled-badge mt-2">
                                        <i className="fas fa-link me-1"></i>Bundled
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Price and Quantity Row */}
                                <div className="row g-2">
                                  <div className="col-6">
                                    <div className="text-center">
                                      <div><strong>Price</strong></div>
                                      <div className="h5 mb-0 text-primary">{formatCurrency(item.price_at_time)}</div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="text-center">
                                      <div><strong>Quantity</strong></div>
                                      {item.is_bundled_item ? (
                                        <div className="h5 mb-0 text-primary">{item.quantity}</div>
                                      ) : (
                                        <div className="d-flex justify-content-center align-items-center mt-1">
                                          <QuantityInput
                                            value={item.quantity}
                                            onChange={(newQuantity: number) => updateQuantity(item.id, newQuantity)}
                                            min={0}
                                            disabled={updatingItems.has(item.id)}
                                            size="sm"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Subtotal */}
                                <div className="text-center mt-3 pt-2 border-top">
                                  <strong>Subtotal: {formatCurrency(item.subtotal)}</strong>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Desktop Table Layout
                        <div style={{ overflowX: 'auto' }}>
                          <table className="table table-hover mb-0" style={{ minWidth: '800px' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '45%', minWidth: '300px' }}>Product</th>
                                <th style={{ width: '12%', minWidth: '80px' }} className="text-center">Price</th>
                                <th style={{ width: '18%', minWidth: '120px' }} className="text-center">Quantity</th>
                                <th style={{ width: '15%', minWidth: '100px' }} className="text-end">Subtotal</th>
                                <th style={{ width: '10%', minWidth: '60px' }} className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map(item => (
                                <tr key={item.id} className={item.is_bundled_item ? 'bundled-row' : ''}>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <div className="d-flex align-items-center">
                                      <div className="me-3" style={{ width: '60px', height: '60px', minWidth: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                                        <SmartImage
                                          src={item.product.image_url || `/images/products/${item.product.id}`}
                                          alt={item.product.name}
                                          className="cart-item-image"
                                          productName={item.product.name}
                                          productSku={item.product.sku}
                                        />
                                      </div>
                                      <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                                        <div className="d-flex flex-wrap align-items-center mb-1">
                                          <Link 
                                            to={`/products/${item.product.id}`}
                                            className="text-decoration-none"
                                            style={{ cursor: 'pointer' }}
                                          >
                                            <h6 className="mb-0 me-2 text-primary" style={{ lineHeight: '1.3' }}>
                                              {item.product.description || item.product.fullname || item.product.name}
                                            </h6>
                                          </Link>
                                          {item.is_bundled_item && (
                                            <span className="badge bundled-badge small ms-2">
                                              <i className="fas fa-link me-1"></i>Bundled
                                            </span>
                                          )}
                                        </div>
                                        <small className="text-muted">{item.product.sku}</small>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                    <strong>{formatCurrency(item.price_at_time)}</strong>
                                  </td>
                                  <td style={{ verticalAlign: 'middle' }}>
                                    {item.is_bundled_item ? (
                                      <div className="text-center">
                                        <strong>{item.quantity}</strong>
                                      </div>
                                    ) : (
                                      <div className="d-flex justify-content-center align-items-center">
                                        <QuantityInput
                                          value={item.quantity}
                                          onChange={(newQuantity: number) => updateQuantity(item.id, newQuantity)}
                                          min={0}
                                          disabled={updatingItems.has(item.id)}
                                          size="md"
                                        />
                                      </div>
                                    )}
                                  </td>
                                  <td className="text-end" style={{ verticalAlign: 'middle' }}>
                                    <strong>{formatCurrency(item.subtotal)}</strong>
                                  </td>
                                  <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                    {item.is_bundled_item ? (
                                      <i className="fas fa-lock text-muted" style={{ fontSize: '18px' }} title="Cannot remove bundled items"></i>
                                    ) : (
                                      <button
                                        onClick={() => removeItem(item.id, item.is_bundled_item)}
                                        title="Remove item"
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#dc3545',
                                          cursor: 'pointer',
                                          padding: '8px',
                                          fontSize: '18px'
                                        }}
                                      >
                                        <i className="fas fa-trash-alt"></i>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="order-summary-section" style={{
                  flex: isDesktop ? '0 0 380px' : '0 0 100%',
                  position: isDesktop ? 'sticky' : 'static',
                  top: isDesktop ? '100px' : 'auto',
                  height: 'fit-content'
                }}>
                  <div className="card" style={{
                    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                    border: 'none',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div className="card-header" style={{
                      backgroundColor: '#4D64C1',
                      color: 'white',
                      padding: '18px 20px',
                      borderBottom: 'none'
                    }}>
                      <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '18px' }}>
                        Order Summary
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Subtotal ({cart?.item_count} items):</span>
                        <span>{formatCurrency(cart?.subtotal || 0)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Shipping:</span>
                        <span>{formatCurrency(Math.max(shippingMinimumDollars, (cart?.subtotal || 0) * shippingPercentage))}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Tax:</span>
                        <span className="text-muted small">TBD</span>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between mb-4">
                        <strong>Total:</strong>
                        <strong className="text-primary">
                          {formatCurrency((cart?.subtotal || 0) + Math.max(shippingMinimumDollars, (cart?.subtotal || 0) * shippingPercentage))}
                          <small className="text-muted"> + tax</small>
                        </strong>
                      </div>
                      
                      {cart && cart.subtotal < minimumOrderAmount && (
                        <div className="alert alert-warning mb-3 text-start">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          <small>Minimum order amount is ${minimumOrderAmount.toFixed(2)}. Add ${formatCurrency(minimumOrderAmount - cart.subtotal).replace('$', '')} more to proceed to checkout.</small>
                        </div>
                      )}

                      {cart && cart.subtotal < minimumOrderAmount && (
                        <div className="alert alert-info mb-3 text-start">
                          <i className="fas fa-info-circle me-2"></i>
                          <small>Minimum order amount for draft is ${minimumOrderAmount.toFixed(2)}. Add ${formatCurrency(minimumOrderAmount - cart.subtotal).replace('$', '')} more to create a draft.</small>
                        </div>
                      )}
                      
                      <div className="d-grid gap-2">
                        <button
                          className="btn"
                          onClick={() => navigate('/checkout')}
                          disabled={!cart || cart.subtotal < minimumOrderAmount}
                          style={{
                            backgroundColor: 'var(--color-success, #2E7D32)',
                            color: 'white',
                            border: 'none',
                            height: '48px',
                            fontSize: '16px',
                            fontWeight: '600'
                          }}
                        >
                          Proceed to Checkout
                        </button>
                        <button
                          className="btn"
                          onClick={() => navigate('/products')}
                          style={{
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            border: '1px solid #dee2e6',
                            height: '48px',
                            fontSize: '16px',
                            fontWeight: '600'
                          }}
                        >
                          Continue Shopping
                        </button>
                        <button
                          className="btn"
                          onClick={() => setShowDraftModal(true)}
                          disabled={!cart || (cart.subtotal < minimumOrderAmount)}
                          style={{
                            backgroundColor: '#4D64C1',
                            color: 'white',
                            border: 'none',
                            height: '48px',
                            fontSize: '16px',
                            fontWeight: '600'
                          }}
                        >
                          Create Draft
                        </button>
                      </div>
                      <div className="alert alert-info" style={{ marginTop: '20px', textAlign: 'left' }}>
                        <i className="fas fa-info-circle me-2"></i>
                        Note: You can send Drafts to anyone in your organization for approval or payment.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}
      </div>
      
      {showDraftModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-file-alt me-2"></i>Create Draft
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDraftModal(false)}
                  disabled={creatingDraft}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Notes (optional):</label>
                  <textarea
                    className="form-control"
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    placeholder="Add any special instructions or notes..."
                    rows={4}
                  />
                </div>
                <div className="alert alert-info" style={{ textAlign: 'left' }}>
                  <i className="fas fa-info-circle me-2"></i>
                  Note: You can send Drafts to anyone in your organization for approval or payment.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDraftModal(false)}
                  disabled={creatingDraft}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={createDraft}
                  disabled={creatingDraft}
                >
                  {creatingDraft ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating...
                    </>
                  ) : (
                    'Create Draft'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cart-item-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
      `}</style>
    </Layout>
  );
};